import { AppstoreAddOutlined, DeleteOutlined, DownOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, Space } from 'antd';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type AdyenPaymentModuleSpecsFragment } from '../../../../api/fragments/AdyenPaymentModuleSpecs';
import { type PermissionValues } from '../../../../utilities/hasPermissions';
import useDeleteModule from '../useDeleteModule';
import { useAdyenPaymentSettingsDrawer } from './AdyenPaymentSettingsDrawer';
import { useUpdateAdyenPaymentModuleDrawer } from './UpdateAdyenPaymentModuleDrawer';

export type AdyenPaymentModuleActionsProps = {
    module: AdyenPaymentModuleSpecsFragment;
    permissions: Pick<PermissionValues, 'hasDeletePermission' | 'hasUpdatePermission'>;
};

const AdyenPaymentModuleActions = ({ module, permissions }: AdyenPaymentModuleActionsProps) => {
    const { t } = useTranslation(['adyenPaymentModuleDetails', 'moduleDetails']);
    const deleteModule = useDeleteModule(module.id);
    const addAdyenPaymentSettingsDrawer = useAdyenPaymentSettingsDrawer(module.id);

    const initialValues = useMemo(() => pick(['displayName'], module), [module]);
    const updateAdyenPaymentModuleDrawer = useUpdateAdyenPaymentModuleDrawer(module.id, initialValues);
    const { hasUpdatePermission, hasDeletePermission } = permissions;

    const items = [
        hasUpdatePermission && {
            label: t('adyenPaymentModuleDetails:mainActionButtons.updateSpecs'),
            key: 'updateSpecs',
            icon: <EditOutlined />,
            onClick: updateAdyenPaymentModuleDrawer.open,
        },
        hasUpdatePermission && {
            label: t('adyenPaymentModuleDetails:mainActionButtons.addSetting'),
            key: 'addSetting',
            icon: <AppstoreAddOutlined />,
            onClick: addAdyenPaymentSettingsDrawer.open,
        },
        hasDeletePermission && {
            label: t('adyenPaymentModuleDetails:mainActionButtons.delete'),
            key: 'delete',
            icon: <DeleteOutlined />,
            onClick: deleteModule,
            danger: true,
        },
    ].filter(Boolean);

    if (!items.length) {
        return null;
    }

    return (
        <>
            <Dropdown overlay={<Menu items={items} />}>
                <Button type="primary">
                    <Space>
                        <span>{t('adyenPaymentModuleDetails:mainActionButtons.actions')}</span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
            {addAdyenPaymentSettingsDrawer.render()}
            {updateAdyenPaymentModuleDrawer.render()}
        </>
    );
};

export default AdyenPaymentModuleActions;
